import React from "react";
import { NavDropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { apiClient } from "../../api/client";
import { useAuth } from "../providers/AuthProvider";

// Metadatos por rol: etiqueta, icono y ruta "home" a la que llevar al cambiar.
const ROLE_META = {
  OWNER: { label: "Propietario", icon: "mdi-crown", home: (b) => `${b}/dashboard` },
  TEACHER: { label: "Docente", icon: "mdi-account-tie", home: (b) => `${b}/agenda` },
  STUDENT: {
    label: "Estudiante",
    icon: "mdi-school",
    home: (b) => `${b}/mi-espacio/mis-clases`,
  },
};

/**
 * RoleSwitcher — "Ver como: Propietario / Docente …" para usuarios con rol dual.
 *
 * - Cambia la VISTA activa (nav + guards reaccionan). El backend autoriza por
 *   todos los roles del usuario, así que cambiar de vista no revoca acceso.
 * - Si el usuario es dueño pero aún no docente, ofrece "Activar modo docente"
 *   (crea su membresía TEACHER respetando el límite del plan).
 * - No se muestra para usuarios de un solo rol (salvo el dueño, por la activación).
 */
function RoleSwitcher() {
  const { user, roles, activeRole, setActiveRole, addRole } = useAuth();
  const navigate = useNavigate();
  const base = user?.slug ? `/${user.slug}/app` : null;

  const isOwner = roles.includes("OWNER");
  const isTeacher = roles.includes("TEACHER");
  const showActivate = isOwner && !isTeacher;

  const switchTo = (role) => {
    setActiveRole(role);
    if (base && ROLE_META[role]) navigate(ROLE_META[role].home(base));
  };

  const enableTeaching = useMutation({
    mutationFn: () =>
      apiClient.post("/api/v1/onboarding/me/teaching/").then((r) => r.data),
    onSuccess: (data) => {
      (data?.roles ?? ["TEACHER"]).forEach(addRole);
      switchTo("TEACHER");
      toast.success("Modo docente activado. Ya puedes dictar clases.");
    },
    onError: (err) => {
      const d = err?.response?.data;
      toast.error(d?.message || d?.detail || "No se pudo activar el modo docente.");
    },
  });

  if (!base) return null;
  if (roles.length <= 1 && !showActivate) return null;

  const current = ROLE_META[activeRole] ?? ROLE_META[roles[0]] ?? {};

  return (
    <NavDropdown
      align="end"
      id="role-switcher"
      title={
        <span className="d-inline-flex align-items-center gap-1">
          <i className={`mdi ${current.icon ?? "mdi-swap-horizontal"}`} aria-hidden="true" />
          <span className="d-none d-md-inline small">{current.label ?? "Ver como"}</span>
        </span>
      }
    >
      <NavDropdown.Header>Ver como</NavDropdown.Header>
      {roles.map((r) => (
        <NavDropdown.Item
          key={r}
          active={r === activeRole}
          onClick={() => switchTo(r)}
        >
          <i className={`mdi ${ROLE_META[r]?.icon ?? "mdi-account"} me-2`} aria-hidden="true" />
          {ROLE_META[r]?.label ?? r}
        </NavDropdown.Item>
      ))}
      {showActivate && (
        <>
          <NavDropdown.Divider />
          <NavDropdown.Item
            onClick={() => enableTeaching.mutate()}
            disabled={enableTeaching.isPending}
          >
            <i className="mdi mdi-plus-circle-outline me-2" aria-hidden="true" />
            Activar modo docente
          </NavDropdown.Item>
        </>
      )}
    </NavDropdown>
  );
}

export { RoleSwitcher };
